import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle, Clock, Calendar, 
  ChevronDown, ChevronUp, User, 
  RotateCcw, Target, Award, MessageSquare, CheckSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

const PrerequisValidation = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStagiaire, setExpandedStagiaire] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [bulkMode, setBulkMode] = useState(false);

  // Les 3 prérequis
  const PREREQUIS = [
    "Parcours en jet à selle",
    "Parcours en jet à bras",
    "Parcours en engins tractés"
  ];

  useEffect(() => {
    loadPrerequisData();
  }, []);

  const loadPrerequisData = async () => {
    try {
      const response = await axios.get('/prerequis/all');
      setStagiaires(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (stagiaireId, prerequis, statut, dateValidation, commentaire, isNewAttempt = false) => {
    try {
      const response = await axios.post('/prerequis/validate', {
        stagiaire_id: stagiaireId,
        prerequis: prerequis,
        statut: statut,
        date_validation: dateValidation,
        commentaire: commentaire,
        is_new_attempt: isNewAttempt
      });
      
      if (response.data.all_validated) {
        toast.success('Tous les prérequis sont validés ! Le stagiaire peut passer BC3.');
      } else {
        toast.success('Prérequis enregistré');
      }
      
      loadPrerequisData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const filteredStagiaires = stagiaires.filter(stag => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'complete') return stag.all_validated;
    if (filterStatus === 'incomplete') return !stag.all_validated;
    return true;
  });

  // Stats globales
  const totalStagiaires = stagiaires.length;
  const stagValidatedAll = stagiaires.filter(s => s.all_validated).length;
  const stagInProgress = totalStagiaires - stagValidatedAll;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="prerequis-validation">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Target className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-display font-semibold text-[#0F172A]">Validation des Prérequis</h2>
              <p className="text-sm text-slate-500">Parcours obligatoires avant le BC3</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stagValidatedAll}</p>
              <p className="text-xs text-slate-500">Prérequis complets</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{stagInProgress}</p>
              <p className="text-xs text-slate-500">En cours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="p-4 border-b border-slate-100 bg-slate-50">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filterStatus === 'all' 
                ? 'bg-purple-600 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="filter-all"
          >
            Tous ({totalStagiaires})
          </button>
          <button
            onClick={() => setFilterStatus('incomplete')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filterStatus === 'incomplete' 
                ? 'bg-orange-500 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="filter-incomplete"
          >
            En cours ({stagInProgress})
          </button>
          <button
            onClick={() => setFilterStatus('complete')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filterStatus === 'complete' 
                ? 'bg-green-500 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="filter-complete"
          >
            Validés ({stagValidatedAll})
          </button>
          <button
            onClick={() => setBulkMode(!bulkMode)}
            className={`ml-auto px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
              bulkMode ? 'bg-[#8DC63F] text-white' : 'bg-white text-[#0F172A] border border-[#8DC63F] hover:bg-green-50'
            }`}
            data-testid="prerequis-bulk-toggle"
          >
            <CheckSquare className="h-4 w-4" /> Validation en bloc
          </button>
        </div>
      </div>

      {bulkMode && (
        <BulkPrerequisGrid stagiaires={filteredStagiaires} PREREQUIS={PREREQUIS} onDone={loadPrerequisData} />
      )}

      {/* Liste des stagiaires */}
      <div className="divide-y divide-slate-100 max-h-[800px] overflow-y-auto">
        {filteredStagiaires.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            Aucun stagiaire trouvé
          </div>
        ) : (
          filteredStagiaires.map((stag) => (
            <div key={stag.stagiaire_id} className="border-b border-slate-100 last:border-b-0">
              {/* Ligne stagiaire */}
              <div 
                className={`p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${
                  stag.all_validated ? 'bg-green-50/50' : ''
                }`}
                onClick={() => setExpandedStagiaire(expandedStagiaire === stag.stagiaire_id ? null : stag.stagiaire_id)}
                data-testid={`stagiaire-row-${stag.stagiaire_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    stag.all_validated ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    <User className={`h-5 w-5 ${stag.all_validated ? 'text-green-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A]">{stag.prenom} {stag.nom}</p>
                    <p className="text-xs text-slate-500">{stag.centre_formation}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                  {/* Progression */}
                  <div className="flex items-center gap-2">
                    <div className="w-12 sm:w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          stag.all_validated ? 'bg-green-500' : 'bg-purple-400'
                        }`}
                        style={{ width: `${(stag.validated_count / stag.total_prerequis) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-600">{stag.validated_count}/{stag.total_prerequis}</span>
                  </div>
                  {stag.all_validated && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full hidden sm:flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Complet
                    </span>
                  )}
                  {expandedStagiaire === stag.stagiaire_id ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Détail des prérequis (expandable) */}
              {expandedStagiaire === stag.stagiaire_id && (
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  <div className="space-y-3">
                    {PREREQUIS.map((prerequis) => {
                      const validation = stag.prerequis[prerequis] || { statut: 'en_attente' };
                      return (
                        <PrerequisRow
                          key={prerequis}
                          prerequis={prerequis}
                          validation={validation}
                          stagiaireId={stag.stagiaire_id}
                          onValidate={handleValidate}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const todayISO = () => new Date().toLocaleDateString('sv-SE');
const isDone = (v) => v && (v.statut === 'valide' || v.statut === 'equivalence');

// Validation en bloc : cocher les cases puis valider avec la date choisie (aujourd'hui par défaut)
const BulkPrerequisGrid = ({ stagiaires, PREREQUIS, onDone }) => {
  const [selected, setSelected] = useState({});
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const key = (sid, p) => `${sid}||${p}`;
  const pending = (stag) => PREREQUIS.filter(p => !isDone(stag.prerequis?.[p]));
  const toggle = (k) => setSelected(prev => ({ ...prev, [k]: !prev[k] }));
  const setMany = (keys, value) => setSelected(prev => { const n = { ...prev }; keys.forEach(k => { n[k] = value; }); return n; });

  const rowKeys = (stag) => pending(stag).map(p => key(stag.stagiaire_id, p));
  const colKeys = (p) => stagiaires.filter(s => !isDone(s.prerequis?.[p])).map(s => key(s.stagiaire_id, p));
  const allKeys = stagiaires.flatMap(rowKeys);
  const allChecked = (keys) => keys.length > 0 && keys.every(k => selected[k]);
  const count = allKeys.filter(k => selected[k]).length;

  const handleSubmit = async () => {
    if (!date) return toast.error('Choisissez une date');
    const todo = allKeys.filter(k => selected[k]).map(k => k.split('||'));
    setSaving(true);
    let ok = 0;
    for (const [sid, p] of todo) {
      try {
        await axios.post('/prerequis/validate', { stagiaire_id: sid, prerequis: p, statut: 'valide', date_validation: date, commentaire: '' });
        ok++;
      } catch (e) { /* continue with the others */ }
    }
    setSaving(false);
    setSelected({});
    if (ok === todo.length) toast.success(`${ok} prérequis validé${ok > 1 ? 's' : ''}`);
    else toast.error(`${ok}/${todo.length} prérequis validés, certains ont échoué`);
    onDone();
  };

  return (
    <div className="p-4 border-b border-slate-200 bg-green-50/40" data-testid="prerequis-bulk-grid">
      <p className="text-xs text-slate-600 mb-3">Cochez les parcours réussis, puis cliquez sur « Valider la sélection ». Les prérequis déjà validés sont grisés.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="py-2 pr-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={allChecked(allKeys)} disabled={!allKeys.length} onChange={(e) => setMany(allKeys, e.target.checked)} className="h-4 w-4 accent-[#8DC63F]" data-testid="bulk-check-all" />
                  Stagiaire
                </label>
              </th>
              {PREREQUIS.map((p, i) => (
                <th key={p} className="py-2 px-2 text-center">
                  <label className="flex flex-col items-center gap-1 cursor-pointer">
                    <span>{p.replace('Parcours en ', '')}</span>
                    <input type="checkbox" checked={allChecked(colKeys(p))} disabled={!colKeys(p).length} onChange={(e) => setMany(colKeys(p), e.target.checked)} className="h-4 w-4 accent-[#8DC63F]" data-testid={`bulk-check-col-${i}`} />
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stagiaires.map(stag => (
              <tr key={stag.stagiaire_id} className="bg-white">
                <td className="py-2 pr-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={allChecked(rowKeys(stag))} disabled={!rowKeys(stag).length} onChange={(e) => setMany(rowKeys(stag), e.target.checked)} className="h-4 w-4 accent-[#8DC63F]" data-testid={`bulk-check-row-${stag.stagiaire_id}`} />
                    <span className="font-medium text-[#0F172A]">{stag.prenom} {stag.nom}</span>
                  </label>
                </td>
                {PREREQUIS.map((p, i) => {
                  const v = stag.prerequis?.[p];
                  const k = key(stag.stagiaire_id, p);
                  return (
                    <td key={p} className="py-2 px-2 text-center">
                      {isDone(v) ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700" title={v.date_validation || ''}>
                          <CheckCircle className="h-4 w-4" />{v.date_validation ? new Date(v.date_validation).toLocaleDateString('fr-FR') : 'Validé'}
                        </span>
                      ) : (
                        <input type="checkbox" checked={!!selected[k]} onChange={() => toggle(k)} className="h-5 w-5 accent-[#8DC63F] cursor-pointer" data-testid={`bulk-check-${stag.stagiaire_id}-${i}`} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
        <label className="text-xs text-slate-600 flex items-center gap-2">
          Date de validation
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 px-2 text-sm border border-slate-200 rounded bg-white" data-testid="bulk-date" />
        </label>
        <Button onClick={handleSubmit} disabled={!count || saving} className="bg-[#8DC63F] hover:bg-[#7db336] text-white" data-testid="bulk-submit">
          <CheckCircle className="h-4 w-4 mr-1.5" />
          {saving ? 'Validation...' : `Valider la sélection (${count})`}
        </Button>
      </div>
    </div>
  );
};

// Composant pour une ligne de prérequis
const PrerequisRow = ({ prerequis, validation, stagiaireId, onValidate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isNewAttemptEditing, setIsNewAttemptEditing] = useState(false);
  const [statut, setStatut] = useState(validation.statut || 'en_attente');
  const [dateValidation, setDateValidation] = useState(validation.date_validation || '');
  const [commentaire, setCommentaire] = useState(validation.commentaire || '');

  // Pour nouvelle tentative
  const [dateNouvelleTentative, setDateNouvelleTentative] = useState('');
  const [commentaireNouvelleTentative, setCommentaireNouvelleTentative] = useState('');

  const handleSave = () => {
    if (!dateValidation && statut !== 'en_attente') {
      toast.error('Veuillez entrer la date de validation');
      return;
    }
    onValidate(stagiaireId, prerequis, statut, dateValidation, commentaire);
    setIsEditing(false);
  };

  const handleNewAttemptSave = (newStatut) => {
    if (!dateNouvelleTentative) {
      toast.error('Veuillez entrer la date');
      return;
    }
    onValidate(stagiaireId, prerequis, newStatut, dateNouvelleTentative, commentaireNouvelleTentative, true);
    setIsNewAttemptEditing(false);
    setDateNouvelleTentative('');
    setCommentaireNouvelleTentative('');
  };

  const getStatusBadge = (s) => {
    switch (s) {
      case 'valide':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Validé</span>;
      case 'rattrapage':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">En rattrapage</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">En attente</span>;
    }
  };

  // Historique des tentatives
  const history = validation.history || [];

  return (
    <div className={`p-4 rounded-lg border ${
      validation.statut === 'valide' || validation.statut === 'equivalence'
        ? 'bg-green-50 border-green-200'
        : validation.statut === 'rattrapage'
        ? 'bg-orange-50 border-orange-200'
        : 'bg-white border-slate-200'
    }`} data-testid={`prerequis-row-${prerequis}`}>
      {/* En-tête du prérequis */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="font-semibold text-sm text-[#0F172A]">{prerequis}</p>
          {validation.attempt_number > 1 && (
            <span className="inline-block mt-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">
              Tentative #{validation.attempt_number}
            </span>
          )}
        </div>
        {!isEditing && !isNewAttemptEditing && (
          <div className="flex gap-2 flex-shrink-0">
            {validation.statut === 'rattrapage' && (
              <Button 
                size="sm" 
                onClick={() => setIsNewAttemptEditing(true)}
                className="text-xs bg-orange-500 hover:bg-orange-600 text-white"
                data-testid={`btn-new-attempt-${prerequis}`}
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Nouvelle tentative
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="text-xs"
              data-testid={`btn-edit-${prerequis}`}
            >
              {validation.statut === 'en_attente' ? 'Valider' : 'Modifier'}
            </Button>
          </div>
        )}
      </div>

      {/* Affichage du statut actuel */}
      {!isEditing && !isNewAttemptEditing && (
        <div className="space-y-2">
          {validation.statut === 'en_attente' ? (
            <div className="p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-center">
              <p className="text-sm text-slate-500">En attente de validation</p>
            </div>
          ) : (
            <div className={`p-3 rounded-lg border ${
              validation.statut === 'rattrapage' 
                ? 'bg-orange-50 border-orange-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getStatusBadge(validation.statut)}
                </div>
                {validation.date_validation && (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    <span>{new Date(validation.date_validation).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
              </div>
              
              {validation.commentaire && (
                <div className="mt-2 p-2 bg-white/50 rounded border border-slate-200">
                  <p className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Commentaire :
                  </p>
                  <p className="text-sm text-slate-700 italic">"{validation.commentaire}"</p>
                </div>
              )}
            </div>
          )}

          {/* Historique des tentatives */}
          {history.length > 1 && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-500 mb-2">Historique des tentatives :</p>
              <div className="space-y-1">
                {history.map((h, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs p-2 bg-slate-100 rounded">
                    <span>Tentative #{idx + 1}</span>
                    <span className={`px-2 py-0.5 rounded ${
                      h.statut === 'valide' || h.statut === 'equivalence' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {h.statut}
                    </span>
                    <span className="text-slate-500">
                      {h.date_validation ? new Date(h.date_validation).toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Formulaire d'édition principal */}
      {isEditing && (
        <div className="mt-3 space-y-3 pt-3 border-t border-slate-200">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date de validation</Label>
              <input
                type="date"
                value={dateValidation}
                onChange={(e) => setDateValidation(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
                data-testid={`input-date-${prerequis}`}
              />
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
                data-testid={`select-statut-${prerequis}`}
              >
                <option value="en_attente">En attente</option>
                <option value="valide">Validé</option>
                <option value="rattrapage">Rattrapage</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Observations..."
              className="w-full mt-1 h-16 px-2 py-1 text-sm border border-slate-200 rounded resize-none"
              data-testid={`textarea-comment-${prerequis}`}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              Annuler
            </Button>
            <Button 
              size="sm" 
              onClick={handleSave} 
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid={`btn-save-${prerequis}`}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* Formulaire pour nouvelle tentative */}
      {isNewAttemptEditing && (
        <div className="mt-3 space-y-3 pt-3 border-t border-orange-200 bg-orange-50/50 p-3 rounded">
          <p className="text-sm font-medium text-orange-700">Nouvelle tentative</p>
          
          <div>
            <Label className="text-xs">Date de la tentative</Label>
            <input
              type="date"
              value={dateNouvelleTentative}
              onChange={(e) => setDateNouvelleTentative(e.target.value)}
              className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
            />
          </div>

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <textarea
              value={commentaireNouvelleTentative}
              onChange={(e) => setCommentaireNouvelleTentative(e.target.value)}
              placeholder="Observations de la nouvelle tentative..."
              className="w-full mt-1 h-16 px-2 py-1 text-sm border border-slate-200 rounded resize-none"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setIsNewAttemptEditing(false)}>
              Annuler
            </Button>
            <Button 
              size="sm" 
              onClick={() => handleNewAttemptSave('rattrapage')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Marquer en rattrapage
            </Button>
            <Button 
              size="sm" 
              onClick={() => handleNewAttemptSave('valide')}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Valider
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrerequisValidation;
